import { fromApiBoolean } from '@liquidweb-dev/club-sauce/types/boolean';
import useAssetNewOrderedAddOns from 'modules/addOns/useAssetNewOrderedAddOns';
import useAssetDetails from 'modules/queries/asset/useDetails';

const useAddOns = (uniqId: string, enabled: boolean = true) => {
	const { data: assetDetailsData, ...rest } = useAssetDetails(
		{
			uniq_id: uniqId,
			alsowith: ['capabilities', 'associationDetails', 'featureDetails'],
		},
		{
			refetchInterval: 30000,
			enabled,
		},
	);

	const {
		associationDetails,
		capabilities,
		featureDetails,
		controlPanel,
		isWindows,
	} = assetDetailsData || {};

	const { assetNewOrderedAddOns, isLoading: isLoadingNewOrderedAddOns } =
		useAssetNewOrderedAddOns(assetDetailsData);
	const getStatus = (code: string, association?: { status: string }) =>
		association?.status ||
		(assetNewOrderedAddOns.some((addOn) => addOn.code === code)
			? 'New'
			: undefined);

	const threatDown = associationDetails?.find(
		(association) => association.type === 'ServerSecurity.ThreatDown',
	);
	const imunify360 = associationDetails?.find(
		(association) => association.type === 'ServerSecurity.Imunify360',
	);
	const corero = associationDetails?.find(
		(association) => association.type === 'DDoS.Server.Protect',
	);
	const serverSecurePlus = associationDetails?.find(
		(association) => association.type === 'ServerSecurePlus',
	);
	const vulnerabilityAssess = associationDetails?.find(
		(association) => association.type === 'Vulnerability.Assess',
	);
	const acronisEDR = associationDetails?.find(
		(association) => association.type === 'AcronisEDR',
	);
	const threatDownStatus = getStatus('ServerSecurity.ThreatDown', threatDown);
	const imunify360Status = getStatus('ServerSecurity.Imunify360', imunify360);
	const coreroStatus = getStatus('DDoS.Server.Protect', corero);
	const acronisEDRStatus = getStatus('AcronisEDR', acronisEDR);
	const mssql = featureDetails?.MsSQL;

	const eset = featureDetails?.WinAV?.value === 'NOD32';

	const serverSecurePlusUniqId = serverSecurePlus?.other_uniq_id;
	const vulnerabilityAssessUniqId = vulnerabilityAssess?.other_uniq_id;
	const mssqlUniqId = mssql?.id;

	const canAddCorero =
		fromApiBoolean(capabilities?.canDDoSServerProtect) && !coreroStatus;
	const canAddImunify360 =
		fromApiBoolean(capabilities?.canImunify360) && !imunify360Status;
	const canAddThreatDown =
		fromApiBoolean(capabilities?.canThreatDown) && !threatDownStatus;
	const canAddAcronisEDR =
		fromApiBoolean(capabilities?.addonAcronisEDR) && !acronisEDRStatus;

	const canShowImunify360Upsell =
		canAddImunify360 && !isWindows && controlPanel;
	const canShowThreatDownUpsell =
		canAddThreatDown && !isWindows && !controlPanel;

	const hasInstalledAddOns = Boolean(
		threatDownStatus ||
			imunify360Status ||
			coreroStatus ||
			mssqlUniqId ||
			serverSecurePlusUniqId ||
			vulnerabilityAssessUniqId ||
			eset ||
			acronisEDRStatus,
	);
	const canInstallAddOns =
		canAddCorero || canAddImunify360 || canAddThreatDown || canAddAcronisEDR;

	/** True if ThreatDown, AcronisEDR, or Imunify360 are installed. Can only have one installed at a time on a server */
	const endpointProtection =
		Boolean(threatDownStatus) ||
		Boolean(acronisEDRStatus) ||
		Boolean(imunify360Status);

	return {
		canAddCorero,
		canAddImunify360,
		canAddThreatDown,
		canAddAcronisEDR,
		canShowImunify360Upsell,
		canShowThreatDownUpsell,
		corero,
		coreroStatus,
		serverSecurePlus,
		vulnerabilityAssess,
		mssql,
		eset,
		hasInstalledAddOns,
		imunify360,
		imunify360Status,
		threatDown,
		threatDownStatus,
		acronisEDR,
		acronisEDRStatus,
		canInstallAddOns,
		regionId: assetDetailsData?.region_id,
		endpointProtection,
		...rest,
		isLoading: rest.isLoading || isLoadingNewOrderedAddOns,
	};
};

export default useAddOns;
